import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from 'firebase/auth';

// Type definitions
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface RequestOptions
    extends Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'params'> {
    headers?: Record<string, string>;
}

export interface ApiError {
    message: string;
    status?: number;
    [key: string]: any;
}

const getBackendUrl = (): string => {
    if (window.location.hostname === 'localhost') {
        return 'http://localhost:8000';
    }
    return 'https://levyl.app';
};

/**
 * Gets the current Firebase ID token for the authenticated user
 * @returns The Firebase ID token, or null if no user is authenticated
 */
const getFirebaseToken = async (): Promise<string | null> => {
    const { currentUser } = getAuth();
    if (!currentUser) return null;
    return currentUser.getIdToken(false);
};

/**
 * Handles the API response
 * @param promise The axios request promise
 * @returns The handled response
 */
const handleRequest = <T = any>(
    promise: Promise<AxiosResponse<T>>
): Promise<T> =>
    promise
        .then((response: AxiosResponse<T>) => response.data)
        .catch((error: AxiosError) => {
            if (error.response) {
                throw error.response.data;
            }
            throw error;
        });

/**
 * Sends the request to the given url with the given payload and returns a promise
 * @param url Url endpoint
 * @param data Data/Params object
 * @param method Request type
 * @param options Additional request options
 * @returns Promise containing the request
 */
export default async function request<T = any>(
    url: string,
    data: Record<string, any> = {},
    method: HttpMethod = 'GET',
    options: RequestOptions = {}
): Promise<T> {
    let backendUrl = getBackendUrl();
    backendUrl = `${backendUrl}${url}`;

    // Get Firebase token for authenticated requests
    const headers: Record<string, string> = { ...options.headers };
    try {
        const token = await getFirebaseToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    } catch (error) {
        // Continue without token for public routes
    }

    const requestOptions: AxiosRequestConfig = {
        method,
        url: backendUrl,
        headers,
        ...options
    };

    if (method.toLowerCase() === 'get') {
        requestOptions.params = data;
    } else {
        requestOptions.data = data;
    }

    return handleRequest<T>(axios(requestOptions));
}

/**
 * Makes a GET request and returns a promise
 * @param url Endpoint url
 * @param params Query params
 * @returns Result Promise
 */
export function get<T = any>(
    url: string,
    params: Record<string, any> = {}
): Promise<T> {
    return request<T>(url, params, 'GET');
}

/**
 * Makes a POST request and returns a promise
 * @param url Endpoint url
 * @param data Payload data
 * @param options Additional options
 * @returns Response promise
 */
export function post<T = any>(
    url: string,
    data?: Record<string, any>,
    options: RequestOptions = {}
): Promise<T> {
    return request<T>(url, data, 'POST', options);
}

/**
 * Makes a PUT request and returns a promise
 * @param url Endpoint url
 * @param data Payload data
 * @returns Response promise
 */
export function put<T = any>(
    url: string,
    data?: Record<string, any>
): Promise<T> {
    return request<T>(url, data, 'PUT');
}

/**
 * Makes a DELETE request and returns a promise
 * @param url Endpoint url
 * @param data Payload data
 * @param options Additional options
 * @returns Response promise
 */
export function del<T = any>(
    url: string,
    data?: Record<string, any>,
    options: RequestOptions = {}
): Promise<T> {
    return request<T>(url, data, 'DELETE', options);
}

export function patch<T = any>(
    url: string,
    data?: Record<string, any>
): Promise<T> {
    return request<T>(url, data, 'PATCH');
}
